import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

// Setting keys
const BRANDING_SETTINGS = {
  PRIMARY_COLOR: 'branding_primary_color',
  SECONDARY_COLOR: 'branding_secondary_color',
  ACCENT_COLOR: 'branding_accent_color',
  FAVICON_URL: 'branding_favicon_url',
  CUSTOM_CSS: 'branding_custom_css',
} as const

// Schema
const brandingSchema = z.object({
  primary_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color').optional().or(z.literal('')),
  secondary_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color').optional().or(z.literal('')),
  accent_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color').optional().or(z.literal('')),
  favicon_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  custom_css: z.string().optional(),
})

type BrandingFormData = z.infer<typeof brandingSchema>

export function BrandingPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch account settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Form
  const form = useForm<BrandingFormData>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {
      primary_color: '#000000',
      secondary_color: '#666666',
      accent_color: '#0066cc',
      favicon_url: '',
      custom_css: '',
    },
  })

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        primary_color: settings[BRANDING_SETTINGS.PRIMARY_COLOR] || '#000000',
        secondary_color: settings[BRANDING_SETTINGS.SECONDARY_COLOR] || '#666666',
        accent_color: settings[BRANDING_SETTINGS.ACCENT_COLOR] || '#0066cc',
        favicon_url: settings[BRANDING_SETTINGS.FAVICON_URL] || '',
        custom_css: settings[BRANDING_SETTINGS.CUSTOM_CSS] || '',
      })
    }
  }, [settings, form])

  // Mutation
  const mutation = useMutation({
    mutationFn: async (data: BrandingFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: BRANDING_SETTINGS.PRIMARY_COLOR,
          value: data.primary_color || null,
          description: 'Brand primary color',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BRANDING_SETTINGS.SECONDARY_COLOR,
          value: data.secondary_color || null,
          description: 'Brand secondary color',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BRANDING_SETTINGS.ACCENT_COLOR,
          value: data.accent_color || null,
          description: 'Brand accent color',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BRANDING_SETTINGS.FAVICON_URL,
          value: data.favicon_url || null,
          description: 'Favicon URL',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: BRANDING_SETTINGS.CUSTOM_CSS,
          value: data.custom_css || null,
          description: 'Custom CSS overrides',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Branding saved',
        description: 'Your branding settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Branding & Appearance</CardTitle>
          <CardDescription>
            Customize the look and feel of your customer-facing website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
              className="space-y-6"
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="primary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Color</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            className="h-10 w-14 cursor-pointer p-1"
                            value={field.value || '#000000'}
                            onChange={field.onChange}
                          />
                          <Input
                            placeholder="#000000"
                            {...field}
                            className="flex-1"
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Main brand color
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="secondary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secondary Color</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            className="h-10 w-14 cursor-pointer p-1"
                            value={field.value || '#666666'}
                            onChange={field.onChange}
                          />
                          <Input
                            placeholder="#666666"
                            {...field}
                            className="flex-1"
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Secondary brand color
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="accent_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Accent Color</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            className="h-10 w-14 cursor-pointer p-1"
                            value={field.value || '#0066cc'}
                            onChange={field.onChange}
                          />
                          <Input
                            placeholder="#0066cc"
                            {...field}
                            className="flex-1"
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Accent/highlight color
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="favicon_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Favicon URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://example.com/favicon.ico"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      URL to your favicon image (browser tab icon)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="custom_css"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom CSS</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={`.my-class {\n  color: #333;\n}`}
                        className="min-h-[150px] font-mono text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Advanced: Add custom CSS to override default styles
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
